<script lang="ts">
  /**
   * A curses room's loadout: pick three of the four curses, here, once. The
   * server alone decides when one of them fires. A game part the lobby
   * templates take as a snippet (`LobbyTemplate`'s `extras`, the vs-AI start
   * card's content; `docs/decisions/0054`): the shell's `ChoiceCard`s as
   * toggles, under the shell's `SectionLabel`, with a `Note` that says what
   * the pick means.
   *
   * Presentational: the working selection is the room's (`DuelScreen`), and
   * every press is reported.
   */
  import { ChoiceCard, Note, SectionLabel } from '@artibens/app-shell';
  import {
    CURSE_LOADOUT_SIZE,
    describeCurseEffect,
    describeCurseEffectDetail,
  } from '@interlock/protocol';
  import { CURSE_EFFECTS } from '@interlock/sim';
  import type { CurseEffect } from '@interlock/sim';

  import { explainCurseEffect } from '../../copy/explanations.js';

  interface Props {
    readonly loadout: readonly CurseEffect[];
    /** This seat has a loadout on the server already. */
    readonly loadoutSent: boolean;
    readonly onToggle: (effect: CurseEffect) => void;
  }
  const { loadout, loadoutSent, onToggle }: Props = $props();

  /** A curse's own explanation without the "Fog — " it opens on: the card's name already says it. */
  function curseDetail(effect: CurseEffect): string {
    const text = explainCurseEffect(effect);
    const dash = text.indexOf('— ');
    const body = dash === -1 ? text : text.slice(dash + 2);
    return body.charAt(0).toUpperCase() + body.slice(1);
  }
</script>

<div class="loadout" data-testid="curse-loadout">
  <SectionLabel variant="picker">Your loadout · pick {CURSE_LOADOUT_SIZE}</SectionLabel>
  <div class="loadout-picker" data-testid="curse-loadout-picker">
    {#each CURSE_EFFECTS as effect (effect)}
      <ChoiceCard
        kind="toggle"
        selected={loadout.includes(effect)}
        disabled={!loadout.includes(effect) && loadout.length >= CURSE_LOADOUT_SIZE}
        name={describeCurseEffect(effect)}
        tagline={describeCurseEffectDetail(effect)}
        detail={curseDetail(effect)}
        testid="curse-loadout-chip"
        onclick={() => {
          onToggle(effect);
        }}
      />
    {/each}
  </div>
  <Note size="sm" centered>
    {#if loadoutSent}
      Picked. Tap to change before you ready up.
    {:else}
      Pick {CURSE_LOADOUT_SIZE} of the four. One of them fires, at random, whenever you clear a double
      or better.
    {/if}
  </Note>
</div>

<style>
  .loadout,
  .loadout-picker {
    display: flex;
    flex-direction: column;
    gap: var(--ui-space-2);
  }
</style>
